import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import type { OnlyIdRequiredInterface } from '@zaoblako/shared/common/interfaces/only-id-required.interface';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { HelpdeskTaskStatusFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-status-find.dto';
import type { HelpdeskTaskTypeFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';
import {
  HelpdeskTaskPersonalAccountCreateDto,
  HelpdeskTaskPersonalAccountCreateInterface
} from '@zaoblako/shared/modules/helpdesk/dto/task/helpdesk-task-personal-account-create.dto';
import { isHelpdeskTaskPersonalAccountCoreFieldsLocked } from '@zaoblako/shared/modules/helpdesk/constants/helpdesk-task-personal-account-locked-core-field-statuses.constants';
import { HelpdeskTaskStatusEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.status.enum';
import { EAV_ATTRIBUTE_TYPES_EXCLUDED_IN_PERSONAL_ACCOUNT } from '@zaoblako/shared/modules/eav/constants/eav-attribute-types-excluded-in-personal-account.constants';
import { EntityValidatorClass } from '@zaoblako/shared/modules/eav/classes/entity-validator.class';
import type { EavAttributeFindInterface } from '@zaoblako/shared/modules/eav/dtos/attribute/eav-attribute-find.dto';
import type { EavEntityFormInterface } from '@zaoblako/shared/modules/eav/dtos/entity/eav-entity-form.dto';
import { EavAttributeValueTypeEnum } from '@zaoblako/shared/modules/eav/enums/eav-attribute-value-type.enum';
import { EavPersonalAccountHelperClass } from '@zaoblako/shared/modules/eav/classes/eav-personal-account-helper.class';
import { HdEavFieldsComponent } from '@zaoblako/web/shared/dumbs/hd-eav-fields/hd-eav-fields.component';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file/file-find.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FileService } from '@zaoblako/web/core/services/file.service';
import { PersonalAccountHdTaskService } from '@zaoblako/web/core/services/lk/personal-account-hd-task.service';
import { HdPrefixService } from '@zaoblako/web/core/services/prefix/hd-prefix.service';
import { FilesListComponent } from '@zaoblako/web/shared/components/files-list/files-list.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { HdTaskTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/hd-task-type-single-select/hd-task-type-single-select.component';
import { PersonalAccountExternalUserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/personal-account-external-user-multi-select/personal-account-external-user-multi-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { FileUpload, FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { forkJoin, Observable } from 'rxjs';

@Component({
  selector: 'crm-lk-eu-helpdesk-task-form',
  host: { class: 'frm-filters-ui-v2' },
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    CardModule,
    EditorComponent,
    JoinPipe,
    HdTaskTypeSingleSelectComponent,
    ButtonComponent,
    FileUploadModule,
    FilesListComponent,
    PersonalAccountExternalUserMultiSelectComponent,
    HdEavFieldsComponent
  ],
  templateUrl: './lk-eu-helpdesk-task-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LkEuHelpdeskTaskFormComponent
  extends BaseFormComponent<HelpdeskTaskPersonalAccountCreateInterface>
  implements OnInit {
  @Input() public readonly index: number;
  public task: HelpdeskTaskPersonalAccountCreateInterface = <any>{};
  public dto = HelpdeskTaskPersonalAccountCreateDto;

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  @ViewChild('fileUpload') public fileUpload: FileUpload;
  public filesLoading = false;

  public taskTypes: HelpdeskTaskTypeFindInterface[] = [];

  /** Status assigned to new tasks (used for EAV required-on-status rules on create). */
  private newTaskStatus?: HelpdeskTaskStatusFindInterface;

  public readonly eavExcludedInPa = EAV_ATTRIBUTE_TYPES_EXCLUDED_IN_PERSONAL_ACCOUNT as unknown as EavAttributeValueTypeEnum[];

  public get HelpdeskTaskStatusEnum() {
    return HelpdeskTaskStatusEnum;
  }

  public get lkEavAttributes(): EavAttributeFindInterface[] {
    return EavPersonalAccountHelperClass.getInstance().filterAttributes(this.task?.taskType?.entityType?.attributes);
  }

  /** Current or initial HD status for EAV required-on-status rules. */
  public get hdTaskStatusForEav(): HelpdeskTaskStatusFindInterface | undefined {
    return (this.task as HelpdeskTaskFindInterface)?.status ?? this.newTaskStatus;
  }

  public get hdTaskStatusId(): number | undefined {
    return this.hdTaskStatusForEav?.id;
  }

  public get isCoreFieldsLocked(): boolean {
    return isHelpdeskTaskPersonalAccountCoreFieldsLocked(
      (this.task as HelpdeskTaskFindInterface)?.status?.code
    );
  }

  /** При редактировании заявки тип можно только заменить, не сбросить. */
  public get canClearTaskType(): boolean {
    return !this.formStoreService.getState.forms[this.index]?.entityId;
  }

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly personalAccountHdTaskService: PersonalAccountHdTaskService,
    private readonly fileService: FileService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly errorService: ErrorService,
    private readonly hdPrefixService: HdPrefixService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    forkJoin([
      this.personalAccountHdTaskService.getTaskTypes(),
      this.personalAccountHdTaskService.getTaskStatuses()
    ]).subscribe(([taskTypes, statuses]) => {
      this.taskTypes = taskTypes;
      this.newTaskStatus = statuses.find(({ code }) => code === HelpdeskTaskStatusEnum.NEW);
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.personalAccountHdTaskService
      .findById<HelpdeskTaskFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (task) => {
          this.task = task;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public onTaskTypeChange(taskType: HelpdeskTaskTypeFindInterface) {
    this.task.taskType = taskType;
    this.setEntityType(taskType);
  }

  public setEntityType(taskType: HelpdeskTaskTypeFindInterface) {
    const attributes = EavPersonalAccountHelperClass.getInstance().filterAttributes(taskType?.entityType?.attributes);
    if (!attributes.length) {
      this.task.eavEntity = null;
      this.cdRef.markForCheck();
      return;
    }

    const values: Record<number, unknown> = attributes.reduce(
      (acc: Record<number, unknown>, attribute: EavAttributeFindInterface) => {
        acc[attribute.id] = this.task.eavEntity?.values ? this.task.eavEntity.values?.[attribute.id] : null;
        return acc;
      },
      {}
    );
    this.task.eavEntity = {
      ...this.task.eavEntity,
      values
    } as EavEntityFormInterface;
    this.cdRef.markForCheck();
  }

  public saveTask() {
    const lkAttributes = this.lkEavAttributes;
    if (lkAttributes.length && this.task?.eavEntity?.values) {
      if (Object.keys(this.task.eavEntity.values).every((key) => !this.task.eavEntity.values[key])) {
        this.task.eavEntity.values = {};
      }

      const validator = EntityValidatorClass.getInstance().createSafeSyncValidator(lkAttributes, {
        hdTaskStatus: this.hdTaskStatusForEav,
        transformValidationErrors: (values) => ({ eavEntity: { values } }),
        transformValidationMetadata: (nested) => ({
          eavEntity: {
            label: 'Дополнительные реквизиты',
            nested: {
              values: {
                label: 'Значения',
                nested
              }
            }
          }
        })
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_, validationErrors] = validator(this.task.eavEntity.values);
      if (validationErrors?.validationErrors) {
        this.errors = validationErrors.validationErrors as any;
        this.cdRef.markForCheck();
        return;
      }
    }

    if (this.task.eavEntity?.values && this.task.taskType?.entityType?.attributes) {
      this.task.eavEntity = {
        values: EavPersonalAccountHelperClass.getInstance().pickValues(
          this.task.eavEntity.values,
          this.task.taskType.entityType.attributes
        )
      };
    }

    if (this.checkValidate(this.task).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.personalAccountHdTaskService
          .update<HelpdeskTaskPersonalAccountCreateInterface, HelpdeskTaskFindInterface>(this.task)
          .subscribe({
            next: (task) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Заявка ${this.hdPrefixService.getPrefixedNumber(task)} обновлена`);
              this.formStoreService.saveNext(task, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.personalAccountHdTaskService.create<HelpdeskTaskFindInterface>(this.task).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Заявка создана');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router
                .navigate(['/lk/external-user/hd/card/' + this.hdPrefixService.getPrefixedNumber(task)])
                .catch(console.error);
            } else {
              this.formStoreService.saveNext(task, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public uploadFiles(data: FileUploadHandlerEvent) {
    this.filesLoading = true;
    this.cdRef.markForCheck();

    this.fileService.upload(data.files).subscribe({
      next: (files) => {
        this.task.files = this.task.files?.length ? this.task.files.concat(files) : files;
        this.fileUpload.clear();
        this.filesLoading = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.filesLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public deleteFile(files: OnlyIdRequiredInterface<FileFindInterface>[], file: FileFindInterface) {
    this.confirmDialogService.confirm({
      message: `Вы действительно хотите удалить файл "${file.name}"?`,
      accept: () => {
        const index = files.indexOf(file);
        files.splice(index, 1);
        this.cdRef.markForCheck();
      }
    });
  }
}
